import { resetPassword } from "@/app/api/auth-client";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { SMLogo } from "@/app/components/sm-logo";
import { useEffect, useState } from "react";

const REDIRECT_SECONDS = 5;
const MIN_PASSWORD_LENGTH = 8;

function getTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("token")?.trim() || null;
}

export function ResetPasswordPage() {
  const [token] = useState(() => getTokenFromUrl());
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Countdown then redirect to login
  useEffect(() => {
    if (!success || countdown === null) {
      return;
    }
    if (countdown <= 0) {
      window.location.href = "/?after_password_reset=1";
      return;
    }
    const t = setTimeout(
      () => setCountdown((c) => (c === null ? 0 : c - 1)),
      1000,
    );
    return () => clearTimeout(t);
  }, [success, countdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Link inválido ou sem token.");
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError("A senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
      setCountdown(REDIRECT_SECONDS);
    } catch (err) {
      const apiErr = err as { message?: string; error?: string };
      if (
        apiErr.error === "invalid_or_expired_token" ||
        (apiErr as { status?: number }).status === 400
      ) {
        setError(
          "Link inválido ou expirado. Solicite um novo e-mail de redefinição.",
        );
      } else {
        setError(apiErr.message ?? "Não foi possível redefinir a senha.");
      }
    } finally {
      setLoading(false);
    }
  };

  const progressPercent =
    success && countdown !== null
      ? ((REDIRECT_SECONDS - countdown) / REDIRECT_SECONDS) * 100
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SMLogo className="w-20 h-20 drop-shadow-lg" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">
            Redefinir senha
          </h1>
        </div>

        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border space-y-4">
          {success ? (
            <>
              <p className="text-center text-muted-foreground">
                Senha alterada com sucesso.
              </p>
              <div className="space-y-2">
                <div className="h-2 w-full rounded-full bg-primary/20 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-1000 ease-linear"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  Redirecionando para o login em {countdown}{" "}
                  {countdown === 1 ? "segundo" : "segundos"}…
                </p>
              </div>
            </>
          ) : !token ? (
            <>
              <p className="text-center text-muted-foreground">
                Link inválido ou sem token. Use o link que enviamos por e-mail
                ou solicite um novo.
              </p>
              <a
                href="/?after_password_reset=1"
                className="block w-full text-center text-primary hover:underline text-sm py-2"
              >
                Ir para o login
              </a>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm p-3">
                  {error}
                </div>
              )}
              <Input
                type="password"
                placeholder="Nova senha (mín. 8 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
                disabled={loading}
              />
              <Input
                type="password"
                placeholder="Confirmar nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
                disabled={loading}
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Salvando…" : "Redefinir senha"}
              </Button>
              <a
                href="/?after_password_reset=1"
                className="block w-full text-center text-primary hover:underline text-sm py-2"
              >
                Voltar ao login
              </a>
            </form>
          )}
        </div>

        {error &&
          success === false &&
          error.includes("inválido ou expirado") && (
            <p className="text-center mt-4">
              <a
                href="/?after_password_reset=1"
                className="text-primary hover:underline text-sm"
              >
                Esqueceu a senha? Solicite um novo e-mail.
              </a>
            </p>
          )}
      </div>
    </div>
  );
}
